import React, { useCallback, useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	useIsHydrateRenderMode,
	useSsrPlaceholders,
} from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { useProjectsAnalyticsL1 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import { useProjectMenuItemDragAndDrop } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-customise-order/src/controllers/useProjectMenuItemDragAndDrop.tsx';
import { ContainerAvatar as Avatar } from '@atlaskit/side-nav-items/container-avatar';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { ProjectExpandableMenuItem$key } from './__generated__/ProjectExpandableMenuItem.graphql';

type ProjectMenuItemProps = {
	projectData: ProjectExpandableMenuItem$key;
	actionsOnHover: ReactNode;
	children: ReactNode;
	menuItemButtonRef?: React.RefObject<HTMLButtonElement>;
	onToggle?: () => void;
};

const FRAGMENT_KEY = 'jsm-project-menu-item';

// Ideally, reuse this component in Jsw and Jpd project menus.
export function ProjectExpandableMenuItem({
	projectData,
	children,
	actionsOnHover,
	menuItemButtonRef,
	onToggle,
}: ProjectMenuItemProps): React.JSX.Element | null {
	const { sendProjectMenuItemAnalyticsEvent } = useProjectsAnalyticsL1();

	const ssrPlaceholderEnabledRoutes = new Set<RouteName>([
		'servicedesk-queues-custom-view',
		'servicedesk-practice-custom-and-issue',
		'csm-queues-custom-view',
	]);

	const ssrPlaceholderEnabledRoutesOld = new Set<RouteName>([
		'servicedesk-queues-custom-view',
		'servicedesk-practice-custom-and-issue',
	]);

	const shouldWrapWithPlaceholder = useIsActiveRoute(
		fg('csm_appification_standalone_navigation')
			? ssrPlaceholderEnabledRoutes
			: ssrPlaceholderEnabledRoutesOld,
	);

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const project = useFragment<ProjectExpandableMenuItem$key>(
		graphql`
			fragment ProjectExpandableMenuItem on JiraProject {
				avatar @required(action: THROW) {
					large @required(action: THROW)
				}
				name @required(action: THROW)
				projectKey: key @required(action: THROW)
				projectId @required(action: THROW)
				isFavourite
				projectType
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		projectData,
	);

	const onTriggerClick = useCallback(() => {
		sendProjectMenuItemAnalyticsEvent({
			starred: project.isFavourite ?? false,
			recent: !project.isFavourite,
			projectId: project.projectId,
			projectType: project.projectType,
		});
	}, [
		sendProjectMenuItemAnalyticsEvent,
		project?.isFavourite,
		project?.projectId,
		project?.projectType,
	]);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			onToggle && onToggle();
			if (hasExpanded) {
				onTriggerClick();
			}
		},
		[onToggle, onTriggerClick],
	);

	const { state, draggableButtonRef, dropTargetRef, dropIndicator, dragPreview, hasDragIndicator } =
		useProjectMenuItemDragAndDrop({
			projectId: project.projectId,
			projectKey: project.projectKey,
			isFavourite: project.isFavourite,
			name: project.name,
			avatar: project.avatar,
			isEnabled: !!project.isFavourite,
		});

	const isPageHydrating = useIsHydrateRenderMode();
	const displaySsrPlaceholderAttrs = useSsrPlaceholders();

	// `<Avatar src={project.avatar.large} />` is otherwise re-created on every
	// render, giving `elemBefore` a new identity and re-rendering the menu-item
	// trigger subtree unnecessarily (measured via Chippy). `project` is a stable
	// Relay fragment reference, so memoizing on `avatar.large` keeps the element
	// identity stable across parent re-renders.
	const avatarSrc = project?.avatar?.large;
	const elemBefore = useMemo(() => <Avatar src={avatarSrc} />, [avatarSrc]);

	if (!project) {
		return null;
	}

	const menuId = ENTITY_ID.PROJECT(project.projectKey);

	const menuItem = (
		<>
			<Nav4ExpandableMenuItem
				menuId={menuId}
				onExpansionToggle={onExpansionToggle}
				dropIndicator={dropIndicator}
				dropTargetRef={dropTargetRef}
				isDragging={state.type === 'dragging'}
			>
				<ExpandableMenuItemTrigger
					ref={mergeRefs(draggableButtonRef, menuItemButtonRef || null)}
					actionsOnHover={actionsOnHover}
					elemBefore={elemBefore}
					testId={getTestId(menuId)}
					isDragging={state.type === 'dragging'}
					hasDragIndicator={hasDragIndicator}
					interactionName="nav4-sidebar-projects"
				>
					{project.name}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>{children}</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
			{dragPreview}
		</>
	);

	// The data-ssr-placeholder has to be unique for each project in the sidebar
	const FRAGMENT_KEY_NEW = `jsm-project-menu-item-${project.projectKey}`;
	// This is the fallback we need until we have implemented all of the Jira project types.
	return shouldWrapWithPlaceholder ? (
		<div
			data-vc={`${FRAGMENT_KEY}${__SERVER__ && !isPageHydrating ? '-ssr' : ''}`}
			{...(__SERVER__ &&
				displaySsrPlaceholderAttrs && {
					'data-ssr-placeholder': FRAGMENT_KEY_NEW,
				})}
			{...(!__SERVER__ &&
				displaySsrPlaceholderAttrs && {
					'data-ssr-placeholder-replace': FRAGMENT_KEY_NEW,
				})}
		>
			{menuItem}
		</div>
	) : (
		menuItem
	);
}
